// Step logic for the Profile Wizard.
//
// The Share Wizard models its flow as a dynamic `visibleSteps` list — a
// fixed-order sequence whose members drop in and out — so its neighbors are
// `index ± 1` and its progress is `idx / (len + 1)`. The Profile Wizard's shape
// is different: its spine (welcome → attribution → phone → email → summary) never
// varies, and its phone-migration detour is a *diamond*, not a filtered line —
// `phone-migration-proof` forks into two mutually-exclusive proof methods
// (old-phone, or email followed by questions) that reconverge at
// `phone-migration-new-phone`, whose Back returns to the fork (`proof`), NOT the
// method that was used. An `index ± 1` model over any linear ordering gives the
// wrong Back edge there.
//
// So Profile declares an explicit step graph: each step names its Back target and
// its progress value directly. Forward navigation stays imperative in the steps
// (it branches on runtime state — e.g. phone → proof vs → email), exactly as the
// Share Wizard's forward transitions do.

export type ProfileWizardStep =
  | 'welcome'
  // Everything that attributes a shared message to its sender: the familiar and
  // allowed names, plus the preferred pronouns downstream messages use.
  | 'attribution'
  | 'phone'
  // Migration detour off the phone step (#441): prove control of the existing
  // account (one of two methods), then verify the new number.
  | 'phone-migration-proof'
  | 'phone-migration-old-phone'
  | 'phone-migration-email'
  | 'phone-migration-questions'
  | 'phone-migration-new-phone'
  | 'email'
  | 'summary';

const ALL_STEPS = [
  'welcome',
  'attribution',
  'phone',
  'phone-migration-proof',
  'phone-migration-old-phone',
  'phone-migration-email',
  'phone-migration-questions',
  'phone-migration-new-phone',
  'email',
  'summary',
] as const satisfies readonly ProfileWizardStep[];

const PATH_PREFIX = '/profile/steps/';

// Matches the step slug in `/profile/steps/<step>`, tolerating a trailing slash
// or query string (mirrors the Share Wizard's URL-derived current step).
const STEP_PATTERN = /\/profile\/steps\/([a-z-]+)(?:[/?]|$)/;

// The Back target for every step. All back-targets are static — including the
// diamond: the proof methods and the new-phone step all return to the `proof`
// fork — except questions, the second half of the email method, which returns
// to the email step — and `welcome` (the entry step) has no Back.
const BACK = {
  welcome: null,
  attribution: 'welcome',
  phone: 'attribution',
  'phone-migration-proof': 'phone',
  'phone-migration-old-phone': 'phone-migration-proof',
  'phone-migration-email': 'phone-migration-proof',
  'phone-migration-questions': 'phone-migration-email',
  'phone-migration-new-phone': 'phone-migration-proof',
  email: 'phone',
  summary: 'email',
} as const satisfies Record<ProfileWizardStep, ProfileWizardStep | null>;

// Progress-bar value (0–1) for each step. The migration steps are clustered
// between phone (0.5) and email (0.75) so the detour reads as a sub-phase of the
// phone stage rather than as major forward progress.
const PROGRESS: Record<ProfileWizardStep, number> = {
  welcome: 0.0,
  attribution: 0.2,
  phone: 0.5,
  'phone-migration-proof': 0.55,
  'phone-migration-old-phone': 0.6,
  'phone-migration-email': 0.6,
  'phone-migration-questions': 0.65,
  'phone-migration-new-phone': 0.7,
  email: 0.75,
  summary: 1.0,
};

// Short, sentence-case toolbar titles. The `welcome` entry step names the
// wizard itself ("Profile Wizard"); the rest name the field(s) being edited.
const TITLE: Partial<Record<ProfileWizardStep, string>> = {
  welcome: 'Profile Wizard',
  attribution: 'Attribution',
  phone: 'Phone',
  'phone-migration-proof': "Verify it's you",
  'phone-migration-old-phone': 'Old number',
  'phone-migration-email': 'Email code',
  'phone-migration-questions': 'Security questions',
  'phone-migration-new-phone': 'New number',
  email: 'Email',
  summary: 'Review',
};

export const ProfileWizardLogic = {
  allSteps(): readonly ProfileWizardStep[] {
    return ALL_STEPS;
  },

  isStep(value: string): value is ProfileWizardStep {
    return (ALL_STEPS as readonly string[]).includes(value);
  },

  pathForStep(step: ProfileWizardStep): string {
    return `${PATH_PREFIX}${step}`;
  },

  // The step named by a URL path, or `welcome` when the path names no known step
  // (e.g. the bare `/profile` redirect target).
  stepFromPath(path: string): ProfileWizardStep {
    const match = path.match(STEP_PATTERN);
    return match && this.isStep(match[1]) ? match[1] : 'welcome';
  },

  // The step to go back to from `step`, or null for the entry step (`welcome`).
  // Typed per step, so a caller naming any other step gets a non-null result.
  previousStep<S extends ProfileWizardStep>(step: S): (typeof BACK)[S] {
    return BACK[step];
  },

  progressForStep(step: ProfileWizardStep): number {
    return PROGRESS[step];
  },

  titleForStep(step: ProfileWizardStep): string | undefined {
    return TITLE[step];
  },
};

/**
 * The segment value of the attribution step's "None" choice. It is never
 * stored: choosing it clears the preferred pronouns, and it is what the segment
 * shows while none are stored. Pronouns are optional — a story told about a
 * sender who gave none refers to them by name, or as "they".
 */
/**
 * Query param naming a button on the Attribution step that returns the user to
 * the page that sent them into the wizard (see AttributionStep). Its value is
 * the button's sentence-case label.
 */
export const RETURN_BUTTON_LABEL_PARAM = 'returnButtonLabel';

export const NO_PRONOUNS_VALUE = 'none';

/**
 * The preferred-pronoun choices offered on the attribution step. Values are
 * stored (and read by `ReshareStoryLogic.pronounForms`) in the lowercase "subject/object"
 * form the news share fields already use, except `NO_PRONOUNS_VALUE`, which
 * leaves them unset.
 */
export const PRONOUN_OPTIONS = [
  { value: NO_PRONOUNS_VALUE, label: 'None' },
  { value: 'he/him', label: 'He/him' },
  { value: 'she/her', label: 'She/her' },
  { value: 'they/them', label: 'They/them' },
] as const;
